import { useEffect, useState } from "react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { CheckCircle2, Sparkles, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const FOUNDING_FEATURES = [
  "3 months free founder access",
  "No credit card required",
  "Full Command-level access during your founder period",
  "After founder access, choose a plan that fits",
  "Priority support and direct influence on the roadmap",
];

// Phase 2Z.v: customer-facing feature lists by plan. Kept here so
// marketing copy stays in one place. Backend slug `business` is
// rendered with the public label `Command` (set in stripe_service.py).
//
// Phase 2Z.y: public employee limits revised to 10 / 30 / 75 / 75+.
// Limits are presentation-only at this stage; no enforcement in the
// backend, no Stripe / billing logic changes.
const PAID_FEATURES = {
  starter: [
    "Up to 10 active employees",
    "Core rota management",
    "Employees and teams",
    "Leave and swaps",
    "Basic utilisation reports",
    "Manual roster editing",
  ],
  growth: [
    "Up to 30 active employees",
    "Everything in Starter",
    "Rota Style",
    "Pattern Engine",
    "Coverage Guard",
    "Rota Mix Intelligence",
    "Performance Focus and dashboard",
    "CSV and paste import",
    "RMI scope detection",
  ],
  business: [
    "Up to 75 active employees",
    "Everything in Growth",
    "AI-assisted RMI Assistant",
    "Automation V1 and Autopilot module",
    "Automatic rota draft creation",
    "Approval after a manager-defined review window",
    "Auto-publish when configured",
    "Clean leave auto-approval when coverage stays safe",
    "Priority support and advanced control",
  ],
};

const PLAN_FEATURE_ANCHOR = {
  starter: { hash: "starter", label: "See Starter features" },
  growth: { hash: "growth", label: "See Growth features" },
  business: { hash: "command", label: "See Command features" },
};

function gbp(amount) {
  if (amount == null) return null;
  return `£${Number(amount).toFixed(0)}`;
}

function formatDate(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function PricingPage() {
  const [founding, setFounding] = useState(null);
  const [plans, setPlans] = useState([]);
  // Phase 2Z.ac: read the active employee count from /billing/me so we
  // can warn customers about plans that are too small for them. The
  // endpoint is auth-gated; for public visitors this stays null and
  // every tile renders as normal.
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/founding/status").then(r => setFounding(r.data)).catch(() => {});
    api.get("/billing/plans").then(r => setPlans(r.data.plans || [])).catch(() => {});
    api.get("/billing/me").then(r => setMe(r.data)).catch(() => {});
  }, []);

  const paid = ["starter", "growth", "business"]
    .map(slug => plans.find(p => p.slug === slug))
    .filter(Boolean);

  // Active-employee-count tile state. Pure presentation: the same rule
  // is independently enforced server-side by /billing/checkout.
  function planFitState(plan) {
    if (!me || me.active_employees == null) return null;
    const limit = plan?.max_employees;
    if (limit == null) return null;
    if (me.active_employees > limit) {
      const recommended =
        me.active_employees <= 30 ? "Growth"
        : me.active_employees <= 75 ? "Command"
        : "Enterprise";
      return {
        ok: false,
        active: me.active_employees,
        limit,
        recommended,
      };
    }
    return { ok: true, active: me.active_employees, limit };
  }

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      <PublicNav />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 w-full">
        <div className="text-center">
          <div className="eyebrow text-[#4B5773]">Pricing</div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#0B1A36] mt-2">Simple pricing that grows with you.</h1>
          <p className="text-base text-[#4B5773] mt-4 max-w-2xl mx-auto" data-testid="pricing-hero-subtext">
            Every new company gets a 14-day free trial with full Command access, no credit card required. The first 10 founding companies get 3 months free. Pick a plan when you&apos;re ready.
          </p>
        </div>

        {/* Founding Customer Programme - hero */}
        <div className="mt-10 rounded-2xl bg-[#0B1A36] text-white p-8 sm:p-12 relative overflow-hidden"
             data-testid="pricing-founding-card">
          <div className="grid md:grid-cols-12 gap-6 items-center relative z-10">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                <Sparkles className="h-3.5 w-3.5"/> Founding Customer Programme
              </div>
              <h2 className="font-display text-3xl sm:text-4xl mt-3">£0<span className="text-base font-normal text-white/70"> for 3 months</span></h2>
              <p className="text-white/80 text-sm mt-2 max-w-md">
                The first 10 companies to sign up get 3 months free and help shape the platform.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-white/90">
                {FOUNDING_FEATURES.map(t => (
                  <li key={t} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#28C840] shrink-0"/>{t}</li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-white/60">
                Not a founder? Every new company still gets a 14-day free trial, no credit card required.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="rounded-xl bg-white/10 backdrop-blur-sm p-5" data-testid="pricing-founding-slots">
                {founding ? (
                  <>
                    <div className="font-display text-5xl">
                      {founding.claimed}<span className="text-xl font-normal opacity-70"> / {founding.limit}</span>
                    </div>
                    <div className="text-xs uppercase tracking-widest opacity-70 mt-1">places claimed</div>
                    <div className="text-sm opacity-90 mt-3">
                      {founding.closed
                        ? "Programme closed."
                        : <>{founding.slots_left} slot{founding.slots_left === 1 ? "" : "s"} left · ends {formatDate(founding.expiry_date)}</>}
                    </div>
                    {founding.closed ? (
                      <Link to="/start" data-testid="pricing-cta-closed"
                        className="mt-5 inline-flex items-center px-5 py-2.5 rounded-md bg-white/20 text-white text-sm font-semibold hover:bg-white/30">
                        Start a 14-day trial
                      </Link>
                    ) : (
                      <Link to="/start?founder=1" data-testid="pricing-cta"
                        className="mt-5 inline-flex items-center px-5 py-2.5 rounded-md bg-white text-[#0B1A36] text-sm font-semibold hover:bg-slate-100">
                        Claim a founding spot →
                      </Link>
                    )}
                  </>
                ) : (
                  <div className="h-32 grid place-items-center text-white/60 text-sm">Loading…</div>
                )}
              </div>
            </div>
          </div>
          <div className="pointer-events-none absolute -top-12 -right-12 h-60 w-60 rounded-full bg-[#2563EB]/30 blur-3xl"/>
        </div>

        {/* Paid plans */}
        <div className="mt-12">
          <div className="text-center mb-6">
            <div className="eyebrow text-[#4B5773]">Plans</div>
            <h2 className="font-display text-2xl sm:text-3xl text-[#0B1A36] mt-2">Pick the plan that fits your team</h2>
            <p className="text-sm text-[#4B5773] mt-2 max-w-2xl mx-auto" data-testid="pricing-trial-strip">
              Every trial starts with full Command access for 14 days. No credit card required. After your trial, choose the plan that fits your team.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {paid.map(p => {
              const fit = planFitState(p);
              return (
              <div key={p.slug} data-testid={`pricing-plan-${p.slug}`}
                   className="rounded-2xl border border-slate-200 bg-white p-6 flex flex-col">
                <div className="font-display text-lg text-[#0B1A36]">{p.label}</div>
                <div className="font-display text-3xl text-[#0B1A36] mt-2">
                  {gbp(p.price_gbp)}<span className="text-sm font-normal text-[#4B5773]"> /month</span>
                </div>
                {fit && !fit.ok && (
                  <div
                    data-testid={`pricing-plan-too-small-${p.slug}`}
                    className="mt-3 rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-xs"
                  >
                    You have {fit.active} active employees. {p.label} supports up to {fit.limit}. Choose {fit.recommended} or higher.
                  </div>
                )}
                <ul className="mt-4 space-y-2 text-sm text-[#4B5773] flex-1">
                  {(PAID_FEATURES[p.slug] || []).map(f => (
                    <li key={f} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#16A34A] shrink-0"/>{f}</li>
                  ))}
                </ul>
                {PLAN_FEATURE_ANCHOR[p.slug] && (
                  <Link
                    to={`/features#${PLAN_FEATURE_ANCHOR[p.slug].hash}`}
                    data-testid={`pricing-plan-see-features-${p.slug}`}
                    className="mt-3 inline-flex items-center text-sm font-medium text-[#1D4ED8] hover:text-[#0B1A36]"
                  >
                    {PLAN_FEATURE_ANCHOR[p.slug].label}
                  </Link>
                )}
                <Link to="/start" data-testid={`pricing-plan-cta-${p.slug}`}
                  className="mt-3 inline-flex items-center justify-center px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-[#0B1A36] hover:bg-slate-50">
                  Start free trial
                </Link>
              </div>
              );
            })}
            {/* Enterprise tile */}
            <div data-testid="pricing-plan-enterprise" className="rounded-2xl border border-slate-200 bg-[#F6F9FE] p-6 flex flex-col">
              <div className="font-display text-lg text-[#0B1A36]">Enterprise</div>
              <div className="font-display text-3xl text-[#0B1A36] mt-2">Contact us</div>
              <ul className="mt-4 space-y-2 text-sm text-[#4B5773] flex-1">
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#16A34A] shrink-0"/>75+ active employees</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#16A34A] shrink-0"/>Custom onboarding</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#16A34A] shrink-0"/>Advanced support</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#16A34A] shrink-0"/>Future API and Salesforce connector eligibility when available</li>
              </ul>
              <a href="mailto:hello@nextrota.com" data-testid="pricing-plan-cta-enterprise"
                 className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-slate-200 text-sm font-medium text-[#0B1A36] hover:bg-slate-50">
                Contact us <ExternalLink className="h-3.5 w-3.5"/>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center text-sm text-[#4B5773]">
          Questions about plans? <a href="mailto:hello@nextrota.com" className="text-[#2563EB] underline">hello@nextrota.com</a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
